import moment, { Moment } from 'moment-timezone';
import { CustomerAddress } from 'types/Customer';
import { MarketplaceServiceCategory } from 'types/Marketplace';
import {
    OnlineBookingSessionItem,
    OnlineBookingTime,
    SessionBusinessArea
} from 'types/OnlineBooking';
import { Service } from 'types/Service';
import { Staff } from 'types/Staff';
import { parseJSON } from 'utils/json';

export const WALKING_BOOKING_TYPE_PRIVATE = 'private_session';
export const WALKING_BOOKING_TYPE_GROUP = 'group_session';

export type WalkingBookingType =
    | typeof WALKING_BOOKING_TYPE_PRIVATE
    | typeof WALKING_BOOKING_TYPE_GROUP;

export interface WalkingServiceConfig {
    bookingType: WalkingBookingType;
    maxPets: number;
    inHomeService: boolean;
    durationMinutes: number;
}

export interface WalkingEligibilityServiceDto {
    id: number;
    staff_ids: Array<number>;
}

export type WalkingInHomeExcludedReason = 'out_of_zone' | 'pet_count';

export interface WalkingEligibilityDto {
    business_area: string;
    in_home_prompt: boolean;
    in_home_out_of_area: boolean;
    in_home_excluded_reason?: WalkingInHomeExcludedReason | null;
    services: Array<WalkingEligibilityServiceDto>;
    reason: string | null;
}

export interface WalkingStaffAvailabilityEntryDto {
    id: number;
    next_available_date: string | null;
}

export interface WalkingStaffAvailabilityDto {
    staff: Array<WalkingStaffAvailabilityEntryDto>;
    reason: string | null;
}

export interface WalkingAvailableHourDto {
    datetime: string;
    staff_available: Array<number>;
    remaining_capacity: number;
}

export interface WalkingAvailabilityDayDto {
    date: string;
    day_name: string;
    available_hours: Array<WalkingAvailableHourDto>;
}

export interface WalkingAvailabilityDto {
    service_id: number;
    next_available_date: string | null;
    start_date: string;
    end_date: string;
    clamped: boolean;
    days?: Array<WalkingAvailabilityDayDto>;
    reason: string | null;
}

export interface WalkingEligibleService {
    id: number;
    staffIds: Array<number>;
}

export interface WalkingEligibility {
    businessArea: string;
    inHomePrompt: boolean;
    inHomeOutOfArea: boolean;
    inHomeExcludedReason: WalkingInHomeExcludedReason | null;
    services: Array<WalkingEligibleService>;
    reason: string | null;
}

export interface WalkingStaffAvailabilityEntry {
    id: number;
    nextAvailableDate: string | null;
}

export interface WalkingStaffAvailability {
    staff: Array<WalkingStaffAvailabilityEntry>;
    reason: string | null;
}

export interface WalkingAvailableHour {
    datetime: string;
    staffAvailable: Array<number>;
    remainingCapacity: number;
}

export interface WalkingAvailabilityDay {
    date: string;
    dayName: string;
    availableHours: Array<WalkingAvailableHour>;
}

export interface WalkingAvailability {
    serviceId: number;
    nextAvailableDate: string | null;
    startDate: string;
    endDate: string;
    clamped: boolean;
    days: Array<WalkingAvailabilityDay>;
    reason: string | null;
}

export const convertWalkingEligibility = (dto: WalkingEligibilityDto): WalkingEligibility => ({
    businessArea: dto.business_area,
    inHomePrompt: dto.in_home_prompt,
    inHomeOutOfArea: dto.in_home_out_of_area,
    inHomeExcludedReason: dto.in_home_excluded_reason ?? null,
    services: (dto.services ?? []).map(service => ({
        id: service.id,
        staffIds: service.staff_ids ?? []
    })),
    reason: dto.reason ?? null
});

export const convertWalkingStaffAvailability = (
    dto: WalkingStaffAvailabilityDto
): WalkingStaffAvailability => ({
    staff: (dto.staff ?? []).map(entry => ({
        id: entry.id,
        nextAvailableDate: entry.next_available_date
    })),
    reason: dto.reason ?? null
});

export const convertWalkingAvailability = (dto: WalkingAvailabilityDto): WalkingAvailability => ({
    serviceId: dto.service_id,
    nextAvailableDate: dto.next_available_date,
    startDate: dto.start_date,
    endDate: dto.end_date,
    clamped: dto.clamped,
    // `days` is omitted entirely when `reason` is set; default so the date step never throws.
    days: (dto.days ?? []).map(day => ({
        date: day.date,
        dayName: day.day_name,
        availableHours: (day.available_hours ?? []).map(hour => ({
            datetime: hour.datetime,
            staffAvailable: hour.staff_available ?? [],
            remainingCapacity: hour.remaining_capacity
        }))
    })),
    reason: dto.reason ?? null
});

const parseWalkingVariableDuration = (service: Service): Record<string, unknown> => {
    const variableDuration = service.variableDuration;

    if (variableDuration && !Array.isArray(variableDuration) && 'metadata' in variableDuration) {
        const metadata = variableDuration.metadata;

        if (typeof metadata === 'string' && metadata.length > 0) {
            const parsed = parseJSON<unknown>(metadata, {});

            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                return parsed as Record<string, unknown>;
            }
        }
    }

    return {};
};

export const resolveWalkingServiceConfig = (service: Service): WalkingServiceConfig => {
    const raw = parseWalkingVariableDuration(service);
    const bookingTypeRaw =
        typeof raw.booking_type === 'string' && raw.booking_type.length > 0
            ? raw.booking_type
            : WALKING_BOOKING_TYPE_PRIVATE;
    const bookingType: WalkingBookingType =
        bookingTypeRaw === WALKING_BOOKING_TYPE_GROUP
            ? WALKING_BOOKING_TYPE_GROUP
            : WALKING_BOOKING_TYPE_PRIVATE;
    const n = Number(raw.max_pets);
    const maxPets = n > 0 && Number.isFinite(n) ? n : 1;
    const inHomeService = raw.in_home_service === true;
    const hours = Number(raw.hours);
    const minutes = Number(raw.minutes);
    const durationMinutes =
        (Number.isFinite(hours) ? hours : 0) * 60 + (Number.isFinite(minutes) ? minutes : 0);

    return {
        bookingType,
        maxPets,
        inHomeService,
        durationMinutes
    };
};

export const walkingMetadataHasDurationKeys = (service: Service): boolean => {
    const raw = parseWalkingVariableDuration(service);

    return 'hours' in raw || 'minutes' in raw;
};

export const walkingServiceDurationMinutes = (service: Service): number => {
    if (walkingMetadataHasDurationKeys(service)) {
        return resolveWalkingServiceConfig(service).durationMinutes;
    }

    return (service.durationHours || 0) * 60 + (service.durationMinutes || 0);
};

// Private + At Facility takes several pets one after another with one staff member; the
// backend offers a time only when someone is free for all of them back to back.
export const isWalkingBackToBackService = (service: Service): boolean => {
    const { bookingType, inHomeService } = resolveWalkingServiceConfig(service);

    return bookingType === WALKING_BOOKING_TYPE_PRIVATE && !inHomeService;
};

export const isWalkingServiceBookableForPets = (
    service: Service,
    numberOfPets: number
): boolean => {
    const { bookingType } = resolveWalkingServiceConfig(service);

    if (bookingType === WALKING_BOOKING_TYPE_GROUP || isWalkingBackToBackService(service)) {
        return true;
    }

    return numberOfPets <= 1;
};

export const resolveWalkingCustomerAddressId = (
    addresses?: Array<CustomerAddress>
): number | undefined => {
    if (!addresses?.length) {
        return undefined;
    }

    const mainActive = addresses.find(address => address.main && address.active);
    const firstActive = addresses.find(address => address.active);

    return (mainActive ?? firstActive)?.id;
};

export interface WalkingJoinedService {
    service: Service;
    staffIds: Array<number>;
}

export const joinWalkingEligibleServices = (
    eligible: Array<WalkingEligibleService>,
    catalog: Array<Service>,
    numberOfPets: number
): Array<WalkingJoinedService> => {
    const catalogById = new Map(catalog.map(service => [service.id, service]));
    const visible: Array<WalkingJoinedService> = [];

    eligible.forEach(entry => {
        const service = catalogById.get(entry.id);

        if (!service) {
            console.warn('Walking eligibility skipped unknown service id', entry.id);
            return;
        }

        if (!isWalkingServiceBookableForPets(service, numberOfPets)) {
            return;
        }

        visible.push({ service, staffIds: entry.staffIds });
    });

    return visible;
};

export interface SessionAreaCopy {
    label: string;
    staffNoun: string;
    serviceNoun: string;
    noServicesForAddress: string;
    noServicesForPets: string;
    staffAllUnavailable: string;
}

export const SESSION_AREA_COPY: Record<SessionBusinessArea, SessionAreaCopy> = {
    [MarketplaceServiceCategory.WALKING]: {
        label: 'Walking',
        staffNoun: 'walker',
        serviceNoun: 'walk',
        noServicesForAddress:
            'There are no walking services available for your address. Try another address.',
        noServicesForPets:
            'There are no walking services available for your pets. Please contact the business for assistance.',
        staffAllUnavailable:
            'No walkers are available for this service right now. Please try another service or check back later.'
    },
    [MarketplaceServiceCategory.TRAINING]: {
        label: 'Training',
        staffNoun: 'trainer',
        serviceNoun: 'training',
        noServicesForAddress:
            'There are no training services available for your address. Try another address.',
        noServicesForPets:
            'There are no training services available for your pets. Please contact the business for assistance.',
        staffAllUnavailable:
            'No trainers are available for this service right now. Please try another service or check back later.'
    }
};

export const WALKING_NO_SERVICES_FOR_ADDRESS_COPY =
    SESSION_AREA_COPY[MarketplaceServiceCategory.WALKING].noServicesForAddress;

export const WALKING_NO_SERVICES_FOR_PETS_COPY =
    SESSION_AREA_COPY[MarketplaceServiceCategory.WALKING].noServicesForPets;

export interface WalkingServiceListViewInput {
    walkingInHome: boolean;
    addressId: number | undefined;
    inHomeOutOfArea: boolean;
    inHomeExcludedReason: WalkingInHomeExcludedReason | null;
    servicesLength: number;
    visibleServicesLength: number;
}

export interface WalkingServiceListView {
    showPromptBanner: boolean;
    showAddressCard: boolean;
    showOutOfAreaNotice: boolean;
    showList: boolean;
    noServicesCopy?: string;
}

export const deriveWalkingServiceListView = (
    input: WalkingServiceListViewInput,
    businessArea: SessionBusinessArea = MarketplaceServiceCategory.WALKING
): WalkingServiceListView => {
    const copy = SESSION_AREA_COPY[businessArea];
    const hasAddress = input.addressId != null;
    const backendEmpty = input.servicesLength === 0;
    const hiddenForPets = !backendEmpty && input.visibleServicesLength === 0;

    const excludedForPets = input.inHomeExcludedReason === 'pet_count';

    return {
        showPromptBanner: input.walkingInHome && !hasAddress,
        showAddressCard: input.walkingInHome && hasAddress,
        showOutOfAreaNotice: input.inHomeOutOfArea && input.servicesLength > 0 && !excludedForPets,
        showList: !backendEmpty && !hiddenForPets,
        noServicesCopy:
            backendEmpty && !excludedForPets
                ? copy.noServicesForAddress
                : backendEmpty || hiddenForPets
                ? copy.noServicesForPets
                : undefined
    };
};

export const formatCustomerAddress = (address: CustomerAddress): string => {
    const street = [address.addressLineOne, address.addressLineTwo]
        .filter(part => Boolean(part && part.trim()))
        .join(', ');
    const stateZip = [address.state, address.zipCode].filter(Boolean).join(' ');
    const cityStateZip = [address.city, stateZip].filter(Boolean).join(', ');

    return [street, cityStateZip].filter(Boolean).join(', ');
};

export interface WalkingBookingErrorDetail {
    code?: string;
    appointmentUuid?: string;
}

export const walkingBookingErrorDetail = (
    details: Array<WalkingBookingErrorDetail>,
    walkingItemUuids: Array<string>
): WalkingBookingErrorDetail | undefined => {
    if (walkingItemUuids.length === 0) {
        return undefined;
    }

    const tagged = details.filter(detail => detail.appointmentUuid);

    // Booking-level errors carry no appointment, and a backend that predates the
    // echoed uuid sends none at all: both keep the old "booking has walking" answer.
    if (tagged.length === 0) {
        return details[0] ?? {};
    }

    return tagged.find(detail => walkingItemUuids.includes(detail.appointmentUuid as string));
};

export const resolveSessionBookingErrorTarget = (
    details: Array<WalkingBookingErrorDetail>,
    sessionItems: Array<Pick<OnlineBookingSessionItem, 'uuid' | 'businessArea'>>
): { detail: WalkingBookingErrorDetail; businessArea: SessionBusinessArea } | undefined => {
    const detail = walkingBookingErrorDetail(
        details,
        sessionItems.map(item => item.uuid)
    );

    if (!detail) {
        return undefined;
    }

    const item =
        sessionItems.find(sessionItem => sessionItem.uuid === detail.appointmentUuid) ??
        sessionItems[0];

    return { detail, businessArea: item.businessArea };
};

export const WALKING_STAFF_DATE_FORMAT = 'MMM D, YYYY';

export interface WalkingStaffCard {
    staffId: number;
    nextAvailableDate: string | null;
    selectable: boolean;
}

interface WalkingNoPreferenceCard {
    selectable: boolean;
    nextAvailableDate: string | null;
}

interface WalkingStaffListDerivation {
    staff: Array<WalkingStaffCard>;
    noPreference: WalkingNoPreferenceCard;
    allUnavailable: boolean;
}

export const deriveWalkingStaffCards = (
    requestedStaffIds: Array<number>,
    responses: Array<WalkingStaffAvailability>
): WalkingStaffListDerivation => {
    const staff = requestedStaffIds.map(staffId => {
        const dates = responses.map(response => {
            const entry = response.staff.find(item => item.id === staffId);

            return entry ? entry.nextAvailableDate : null;
        });
        const selectable = responses.length > 0 && dates.every(date => date != null);
        const nonNull = dates.filter((date): date is string => date != null);
        const latest =
            nonNull.length === 0
                ? null
                : nonNull.reduce((latestDate, date) => (date > latestDate ? date : latestDate));

        return { staffId, nextAvailableDate: selectable ? latest : null, selectable };
    });

    const visibleStaff = staff.filter(card => card.selectable);
    const noPreferenceDate =
        visibleStaff.length === 0
            ? null
            : visibleStaff
                  .map(card => card.nextAvailableDate as string)
                  .reduce((earliest, date) => (date < earliest ? date : earliest));

    return {
        staff: visibleStaff,
        noPreference: {
            selectable: visibleStaff.length > 0,
            nextAvailableDate: noPreferenceDate
        },
        allUnavailable: staff.length === 0 || staff.every(card => !card.selectable)
    };
};

const formatWalkingStaffAvailableAt = (date: string): string =>
    moment(date, 'YYYY-MM-DD').format(WALKING_STAFF_DATE_FORMAT);

export const walkingStaffCardDescription = (card: {
    selectable: boolean;
    nextAvailableDate: string | null;
}): string => {
    if (!card.selectable || !card.nextAvailableDate) {
        return 'No availability';
    }

    return `Available: ${formatWalkingStaffAvailableAt(card.nextAvailableDate)}`;
};

export interface WalkingStaffStepView {
    showCards: boolean;
    showUnavailableLine: boolean;
    footerDisabled: boolean;
}

export const deriveWalkingStaffStepView = ({
    allowSelectStaff,
    isLoading,
    fetchError,
    derivation,
    preferredStaffId
}: {
    allowSelectStaff: boolean;
    isLoading: boolean;
    fetchError?: unknown;
    derivation: WalkingStaffListDerivation;
    preferredStaffId?: number;
}): WalkingStaffStepView => {
    const selectedIsValid =
        preferredStaffId != null
            ? derivation.staff.some(card => card.staffId === preferredStaffId)
            : derivation.noPreference.selectable;

    return {
        showCards: allowSelectStaff && !fetchError && (isLoading || !derivation.allUnavailable),
        showUnavailableLine:
            allowSelectStaff && !isLoading && !fetchError && derivation.allUnavailable,
        footerDisabled: !allowSelectStaff || isLoading || Boolean(fetchError) || !selectedIsValid
    };
};

export const applyWalkingPreferredStaff = <T extends OnlineBookingSessionItem>(
    items: Array<T>,
    preferredStaff: Staff | undefined
): Array<T> => items.map(item => ({ ...item, preferredStaff }));

export const clearWalkingItemDateTime = <T extends OnlineBookingSessionItem>(item: T): T => ({
    ...item,
    date: undefined,
    time: undefined
});

export const applyWalkingStaffOnSubmit = <T extends OnlineBookingSessionItem>(
    items: Array<T>,
    preferredStaff: Staff | undefined
): Array<T> =>
    items.map(item => {
        const next = item.staff?.id !== preferredStaff?.id ? clearWalkingItemDateTime(item) : item;

        return { ...next, staff: preferredStaff };
    });

export const clearWalkingItemStaffSelection = <T extends OnlineBookingSessionItem>(item: T): T => ({
    ...item,
    preferredStaff: undefined,
    staff: undefined
});

const dedupeWalkingHours = (hours: Array<WalkingAvailableHour>): Array<WalkingAvailableHour> => {
    const seen = new Set<string>();
    const result: Array<WalkingAvailableHour> = [];

    hours.forEach(hour => {
        if (seen.has(hour.datetime)) {
            return;
        }

        seen.add(hour.datetime);
        result.push(hour);
    });

    return result;
};

export const walkingHoursForDate = (
    availability: WalkingAvailability,
    date: string
): Array<WalkingAvailableHour> => {
    const day = availability.days.find(item => item.date === date);

    return dedupeWalkingHours(day?.availableHours ?? []);
};

const intersectWalkingHoursByDatetime = (
    lists: Array<Array<WalkingAvailableHour>>
): Array<WalkingAvailableHour> => {
    if (lists.length === 0) {
        return [];
    }

    const [first, ...rest] = lists;

    if (rest.length === 0) {
        return dedupeWalkingHours(first);
    }

    return dedupeWalkingHours(first).filter(hour =>
        rest.every(list => list.some(other => other.datetime === hour.datetime))
    );
};

export const walkingHoursForSelectedDay = (
    availabilities: Array<WalkingAvailability>,
    date: string
): Array<WalkingAvailableHour> => {
    if (availabilities.length === 0) {
        return [];
    }

    return intersectWalkingHoursByDatetime(
        availabilities.map(availability => walkingHoursForDate(availability, date))
    );
};

export const formatWalkingHourLabel = (datetime: string, timeZone: string): string =>
    moment.tz(datetime, timeZone).format('h:mm a');

export const buildWalkingTimeFromHour = (
    hour: WalkingAvailableHour,
    timeZone: string,
    preferredStaff: Staff | undefined
): OnlineBookingTime => {
    const local = moment.tz(hour.datetime, timeZone);

    return {
        hour: local.hours(),
        minute: local.minutes(),
        label: formatWalkingHourLabel(hour.datetime, timeZone),
        staffId: preferredStaff?.id,
        datetime: hour.datetime
    };
};

export const isSameWalkingTime = (
    time: OnlineBookingTime | undefined,
    hour: WalkingAvailableHour,
    timeZone: string
): boolean => {
    if (!time) {
        return false;
    }

    const local = moment.tz(hour.datetime, timeZone);

    return local.hours() === time.hour && local.minutes() === time.minute;
};

export const walkingUpdateClearsBookingError = (
    existing: OnlineBookingSessionItem | undefined,
    next: OnlineBookingSessionItem
): boolean => {
    if (!existing) {
        return true;
    }

    return (
        existing.service?.id !== next.service?.id ||
        existing.preferredStaff?.id !== next.preferredStaff?.id ||
        existing.staff?.id !== next.staff?.id ||
        next.time != null
    );
};

export const applyWalkingDateSelection = <T extends OnlineBookingSessionItem>(
    items: Array<T>,
    date: Moment
): Array<T> =>
    items.map(item => ({
        ...item,
        date: date.clone(),
        time: undefined
    }));

export const applyWalkingTimeSelection = <T extends OnlineBookingSessionItem>(
    items: Array<T>,
    date: Moment,
    hour: WalkingAvailableHour,
    timeZone: string,
    preferredStaff: Staff | undefined
): Array<T> => {
    const runPositions = new Map<number, number>();

    return items.map(item => {
        const service = item.service;
        let offsetMinutes = 0;

        if (service && isWalkingBackToBackService(service)) {
            const position = runPositions.get(service.id) ?? 0;

            runPositions.set(service.id, position + 1);
            offsetMinutes = position * walkingServiceDurationMinutes(service);
        }

        const datetime =
            offsetMinutes === 0
                ? hour.datetime
                : moment.parseZone(hour.datetime).add(offsetMinutes, 'minutes').format();

        return {
            ...item,
            date: date.clone(),
            time: buildWalkingTimeFromHour({ ...hour, datetime }, timeZone, preferredStaff),
            staff: preferredStaff
        };
    });
};

export const clearWalkingItemTime = <T extends OnlineBookingSessionItem>(item: T): T => ({
    ...item,
    time: undefined
});

export interface WalkingResumeRow {
    serviceName: string;
    staffLabel: string;
    dateTimeLabel: string;
}

export const deriveWalkingResumeRow = (item: OnlineBookingSessionItem): WalkingResumeRow | null => {
    if (!item.service) {
        return null;
    }

    const dateTimeParts = [
        item.date?.format('MMM DD, YYYY'),
        item.time ? item.time.label : undefined
    ].filter((part): part is string => Boolean(part));

    return {
        serviceName: item.service.name,
        staffLabel: item.staff?.name ?? 'No preference',
        dateTimeLabel: dateTimeParts.join(' - ')
    };
};
